<script setup lang="ts">
import HeadingNameEdit from "../HeadingNameEdit.vue";
import { storedChapterName } from "../../lib/chapterNumbering";
import { useChapterHeadings } from "../../composables/useChapterHeadings";
import {
  fromMaybe,
  just,
  matchMaybe,
  nothing,
  toDomAttr,
  toMaybe,
  isJust,
  isNothing,
  type Maybe,
} from "../../lib/maybe";
import {
  computed,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  useTemplateRef,
  watch,
  type Ref,
} from "vue";
import { storeToRefs } from "pinia";
import { displayFileName } from "../../lib/fileNames";
import { isMapFileName } from "../../lib/mapDocument";
import { openContextMenu } from "../../lib/platform/contextMenu";
import {
  actPrimaryPath,
  chapterFolderFromScenePath,
  chapterPrimaryPath,
  newMapFigureId,
  PROJECT_DATABASE_DIR,
  sceneFileFromScenePath,
  scenePath,
  type ZwAct,
  type ZwChapter,
  type ZwScene,
} from "../../lib/zwConfig";
import { useEditorSessionStore } from "../../stores/useEditorSessionStore";
import { useOutlineEntitiesStore } from "../../stores/useOutlineEntitiesStore";
import { useSceneOpeningsStore } from "../../stores/useSceneOpeningsStore";
import { sceneCustomName, sceneOpeningText } from "../../lib/sceneLabel";
import {
  useOutlineActionsStore,
  type OutlineHeaderNew,
} from "../../stores/useOutlineActionsStore";
import { useProjectWorkspaceStore } from "../../stores/useProjectWorkspaceStore";
import { useDisplayModeStore } from "../../stores/useDisplayModeStore";
import { useRenameEntityFlow } from "../../composables/useRenameEntityFlow";
import {
  useOutlineDrag,
  type DragSource,
  type OutlineRow,
} from "../../composables/useOutlineDrag";
import { assertNever } from "../../lib/assertNever";
import { useTea } from "../../lib/tea";
import {
  initialOutlineCreateModel,
  updateOutlineCreate,
  type OutlineCreateCmd,
} from "./OutlineTree.update";
import ZwButton from "../ui/ZwButton.vue";
import ZwDialog from "../ui/ZwDialog.vue";
import ZwInput from "../ui/ZwInput.vue";

type FocusableInput = {
  focus: (options?: FocusOptions) => void;
  select: () => void;
};

function focusableInputRef(
  raw: Readonly<Ref<unknown>>,
): Ref<Maybe<FocusableInput>> {
  return computed(() =>
    toMaybe(raw.value as InstanceType<typeof ZwInput>),
  ) as Ref<Maybe<FocusableInput>>;
}

const {
  chapterLabel,
  canRenameChapter,
  actLabel,
  canRenameAct,
  actRenamePrefix,
  chapterRenamePrefix,
} = useChapterHeadings();
const emit = defineEmits<{ requestCloseSettings: [] }>();

const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const outlineActions = useOutlineActionsStore();
const entities = useOutlineEntitiesStore();
const display = useDisplayModeStore();
const sceneOpenings = useSceneOpeningsStore();

/** Scene rows always show opening prose (muted italic), never a custom
 * title or "Scene N". Empty body falls back to synopsis, then a placeholder. */
function sceneRowLabel(row: { scene: ZwScene; path: string }): string {
  return sceneOpeningText(
    sceneOpenings.openingFor(row.path),
    row.scene.synopsis,
  );
}

/** Rename seeds the input with the custom name only. a nameless scene starts
 * blank, so committing without typing never turns its snippet into a name. */
function sceneRenameSeed(scene: ZwScene): string {
  return matchMaybe(sceneCustomName(scene), {
    just: (name) => name,
    nothing: () => "",
  });
}
const { isReading: structureLocked } = storeToRefs(display);
const {
  outlineActiveFile: activeFile,
  collapsedChapters,
  collapsedActs,
  activeBasename,
} = storeToRefs(session);

function closeMobileOutline(): void {
  if (session.mobileShell) {
    session.outlineOpen = false;
  }
}

const manuscript = computed(() => session.manuscript);
const chapters = computed(() => session.chapters);

/** Display title for a chapter row — the chapter folder name, not the scene filename. */
function chapterDisplayName(path: string): string {
  const folder = chapterFolderFromScenePath(path);
  return matchMaybe(folder, {
    just: (name) => chapterLabel(name),
    nothing: () => displayFileName(path),
  });
}

function isFileCollapsed(name: string): boolean {
  return collapsedChapters.value.includes(name);
}

function isActCollapsed(path: string): boolean {
  return collapsedActs.value.includes(path);
}

/** Flat render list: an act row, then the chapters under it, each immediately
 * followed by its own scene rows (if expanded and it has 2+). Scenes and
 * act-owned chapters are siblings here, not DOM children; nesting them inside
 * a parent's own `<li>` (via a nested `<ul>`) made pointer-based drag
 * hit-testing unreliable, since a child row's rect always sat inside its
 * parent's rect too. A flat list has no such ambiguity, and the hierarchy is
 * carried by row indentation instead. */
const outlineRows = computed<OutlineRow[]>(() => {
  const rows: OutlineRow[] = [];

  function pushChapter(chapter: ZwChapter, actFolder: Maybe<string>): void {
    const path = chapterPrimaryPath(chapter);
    rows.push({ kind: "chapter", chapter, actFolder, path });
    if (chapter.scenes.length > 1 && isFileCollapsed(path) === false) {
      chapter.scenes.forEach((scene, index) => {
        rows.push({
          kind: "scene",
          chapter,
          actFolder,
          scene,
          index,
          path: scenePath(chapter.folder, scene.file),
        });
      });
    }
  }

  for (const item of manuscript.value) {
    if (item.type === "chapter") {
      pushChapter(item, nothing);
      continue;
    }
    if (item.type === "map") {
      rows.push({
        kind: "map",
        id: item.id,
        title: item.title,
        mapPath: item.mapPath,
      });
      continue;
    }
    const path = actPrimaryPath(item);
    rows.push({ kind: "act", act: item, path });
    if (isActCollapsed(path)) continue;
    for (const chapter of item.chapters) {
      pushChapter(chapter, just(item.folder));
    }
  }
  return rows;
});

/** Unique per-row identity for drag tracking / highlighting — deliberately
 * NOT `row.path`, since a chapter's own row and its first scene's row both
 * resolve to that chapter's first scene path and would otherwise collide. */
function rowKey(row: OutlineRow): string {
  if (row.kind === "act") return `act:${row.act.folder}`;
  if (row.kind === "map") return `map:${row.id}`;
  return row.kind === "chapter"
    ? `chapter:${row.chapter.folder}`
    : `scene:${row.chapter.folder}:${row.scene.file}`;
}

function toggleFile(name: string): void {
  const current = collapsedChapters.value;
  const next = current.includes(name)
    ? current.filter((n) => n !== name)
    : [...current, name];
  void workspace.onSetCollapsedChapters(next);
}

function onSelectFile(name: string): void {
  void workspace.onSelectProjectFile(name);
  void dispatchCreate({ type: "ClearSelectedAct" });
  closeMobileOutline();
}

function isMapRowActive(row: { id: string; mapPath: string }): boolean {
  if (isNothing(activeFile.value) || isNothing(session.rootPath)) return false;
  const active = activeFile.value.value.replace(/\\/g, "/");
  const root = session.rootPath.value.replace(/\\/g, "/").replace(/\/$/, "");
  const abs = `${root}/${row.mapPath}`.replace(/\\/g, "/");
  return active === abs || active.endsWith(`/${row.mapPath}`);
}

function onSelectMap(row: { id: string; mapPath: string }): void {
  void workspace.onSelectManuscriptMap(row.mapPath);
  void dispatchCreate({ type: "ClearSelectedAct" });
  closeMobileOutline();
}

async function onMapContextMenu(
  event: MouseEvent,
  row: { id: string; title: string },
): Promise<void> {
  if (structureLocked.value) return;
  emit("requestCloseSettings");
  await openContextMenu(event, [
    {
      id: `remove-map-${row.id}`,
      label: "Remove from book",
      action: () => {
        void workspace.onRemoveMapFromManuscript(row.id);
      },
    },
  ]);
}

function dragSourceForRow(row: OutlineRow): DragSource {
  if (row.kind === "act") {
    return {
      key: rowKey(row),
      kind: "act",
      folder: row.act.folder,
      sceneFile: nothing,
    };
  }
  if (row.kind === "map") {
    return {
      key: rowKey(row),
      kind: "map",
      folder: row.id,
      sceneFile: nothing,
    };
  }
  if (row.kind === "chapter") {
    return {
      key: rowKey(row),
      kind: "chapter",
      folder: row.chapter.folder,
      sceneFile: nothing,
    };
  }
  return {
    key: rowKey(row),
    kind: "scene",
    folder: row.chapter.folder,
    sceneFile: just(row.scene.file),
  };
}

// --- Acts ---

function onSelectAct(row: { act: ZwAct; path: string }): void {
  void dispatchCreate({ type: "ToggleSelectedAct", folder: row.act.folder });
}

function toggleAct(path: string): void {
  void workspace.onToggleActCollapsed(path);
}

/** Act labels follow project numbering while retaining the saved metadata name. */
function actDisplayName(act: ZwAct): string {
  return actLabel(act.folder);
}

/** Clicking a chapter with 2+ scenes reveals the scene list (if collapsed) and
 * opens its first scene — same as clicking a single-scene chapter used to. */
function onSelectChapter(row: { chapter: ZwChapter; path: string }): void {
  if (row.chapter.scenes.length > 1 && isFileCollapsed(row.path)) {
    toggleFile(row.path);
  }
  onSelectFile(row.path);
}

/** Adding a scene always reveals the (now 2+) scene list, even if this chapter's
 * row was previously left collapsed from an earlier multi-scene state. */
async function onAddScene(row: {
  chapter: ZwChapter;
  path: string;
}): Promise<void> {
  if (structureLocked.value) return;
  await workspace.onNewProjectScene(row.chapter.folder);
  if (collapsedChapters.value.includes(row.path)) {
    void workspace.onSetCollapsedChapters(
      collapsedChapters.value.filter((n) => n !== row.path),
    );
  }
}

async function onAddChapter(row: { act: ZwAct; path: string }): Promise<void> {
  if (structureLocked.value) return;
  await workspace.onNewStoryboardChapter("", just(row.act.folder));
  if (isActCollapsed(row.path)) toggleAct(row.path);
  if (isJust(activeBasename.value)) onSelectFile(activeBasename.value.value);
}

// Immediate creation commands also finish a pending scene drop into a new chapter.

async function runOutlineCreateCmd(cmd: OutlineCreateCmd): Promise<void> {
  switch (cmd.kind) {
    case "clearPendingDrop":
      drag.pendingSceneDrop.value = nothing;
      return;
    case "createAct":
      // A selected act wins over the active scene: you just pointed at where
      // the new act should follow.
      await workspace.onCreateProjectAct(
        cmd.name,
        createModel.value.selectedActFolder,
      );
      return;
    case "createChapter": {
      await workspace.onNewProjectFile(cmd.name);
      // A scene was dragged onto the "new chapter" drop zone — finish by
      // moving the dragged scene into what it made.
      const pendingM = drag.pendingSceneDrop.value;
      if (isJust(pendingM)) {
        drag.pendingSceneDrop.value = nothing;
        const pending = pendingM.value;
        const newFolder = chapterFolderFromScenePath(
          fromMaybe(activeBasename.value) ?? "",
        );
        if (isJust(newFolder)) {
          await workspace.onMoveSceneIntoNewChapter(pending, newFolder.value);
        }
      }
      return;
    }
    case "includeMap": {
      const mapPath = `${PROJECT_DATABASE_DIR}/${cmd.mapFileName}`;
      await workspace.onIncludeMapInManuscript({
        id: newMapFigureId(),
        title: displayFileName(cmd.mapFileName),
        mapPath,
        orientation: 0,
        spread: "single",
        printExtent: "safeZone",
        objectFit: "contain",
      });
      await workspace.onSelectManuscriptMap(mapPath);
      return;
    }
    default:
      assertNever(cmd);
  }
}

const { model: createModel, dispatch: dispatchCreate } = useTea(
  initialOutlineCreateModel,
  updateOutlineCreate,
  { runCmd: just(runOutlineCreateCmd) },
);

const selectedActFolder = computed(() => createModel.value.selectedActFolder);
const includeMapOpen = computed(() => createModel.value.includeMapOpen);
/** Maps in Database that are not already manuscript sequence leaves. */
const availableMapsForBook = computed(() => {
  const placed = new Set(
    manuscript.value
      .filter((item) => item.type === "map")
      .map((item) => item.mapPath),
  );
  return entities.databaseEntries
    .filter((name) => isMapFileName(name))
    .filter((name) => placed.has(`${PROJECT_DATABASE_DIR}/${name}`) === false)
    .slice()
    .sort((a, b) => a.localeCompare(b));
});

function openCreate(kind: "chapter" | "act"): void {
  if (structureLocked.value) return;
  emit("requestCloseSettings");
  void dispatchCreate({ type: "Open", kind });
}

function openIncludeMap(): void {
  if (structureLocked.value) return;
  emit("requestCloseSettings");
  void entities.refreshDatabase();
  void dispatchCreate({ type: "OpenIncludeMap" });
}

async function onNewFile(): Promise<void> {
  openCreate("chapter");
}

function cancelCreate(): void {
  void dispatchCreate({ type: "Cancel" });
}

function closeIncludeMap(): void {
  void dispatchCreate({ type: "CloseIncludeMap" });
}

function includeMap(mapFileName: string): void {
  void dispatchCreate({ type: "IncludeMap", mapFileName });
}

// --- Chapter + scene rename, context menus ---

async function onContextMenu(
  event: MouseEvent,
  chapter: ZwChapter,
): Promise<void> {
  if (structureLocked.value) return;
  emit("requestCloseSettings");

  const name = chapterPrimaryPath(chapter);
  await openContextMenu(event, [
    {
      id: `rename-${name}`,
      label: "Rename",
      disabled: canRenameChapter(chapter.folder) === false,
      action: () => {
        if (canRenameChapter(chapter.folder))
          void chapterRename.start(name, storedChapterName(chapter));
      },
    },
    {
      id: `delete-${name}`,
      label: "Delete",
      action: () => {
        void workspace.onDeleteProjectFile(name);
      },
    },
  ]);
}

async function onSceneContextMenu(
  event: MouseEvent,
  folder: string,
  sceneFile: string,
  currentDisplay: string,
): Promise<void> {
  if (structureLocked.value) return;
  emit("requestCloseSettings");

  const path = scenePath(folder, sceneFile);
  await openContextMenu(event, [
    {
      id: `rename-scene-${folder}-${sceneFile}`,
      label: "Rename scene",
      action: () => {
        void sceneRename.start(path, currentDisplay);
      },
    },
    {
      id: `delete-scene-${folder}-${sceneFile}`,
      label: "Delete scene",
      action: () => {
        void workspace.onDeleteProjectScene(folder, sceneFile);
      },
    },
  ]);
}

async function onActContextMenu(event: MouseEvent, act: ZwAct): Promise<void> {
  if (structureLocked.value) return;
  emit("requestCloseSettings");

  await openContextMenu(event, [
    {
      id: `rename-act-${act.folder}`,
      label: "Rename",
      disabled: canRenameAct(act.folder) === false,
      action: () => {
        if (canRenameAct(act.folder))
          void actRename.start(act.folder, storedChapterName(act));
      },
    },
    {
      id: `delete-act-${act.folder}`,
      label: "Remove act",
      action: () => {
        void workspace.onDeleteProjectAct(act.folder);
      },
    },
  ]);
}

const renameInput = useTemplateRef<InstanceType<typeof ZwInput>>("renameInput");
const chapterRename = useRenameEntityFlow<string>(
  focusableInputRef(renameInput),
  {
    allowEmpty: true,
    onRename: (from, to) => workspace.onRenameProjectFile(from, to),
    afterCommit: nothing,
  },
);

const renameSceneInput =
  useTemplateRef<InstanceType<typeof ZwInput>>("renameSceneInput");
const sceneRename = useRenameEntityFlow<string>(
  focusableInputRef(renameSceneInput),
  {
    allowEmpty: false,
    afterCommit: nothing,
    onRename: (path, to) => {
      const folder = chapterFolderFromScenePath(path);
      const sceneFile = sceneFileFromScenePath(path);
      if (isNothing(folder) || isNothing(sceneFile)) {
        return;
      }
      void workspace.onRenameProjectScene(folder.value, sceneFile.value, to);
    },
  },
);

const renameActInput =
  useTemplateRef<InstanceType<typeof ZwInput>>("renameActInput");
const actRename = useRenameEntityFlow<string>(
  focusableInputRef(renameActInput),
  {
    allowEmpty: true,
    onRename: (from, to) => workspace.onRenameProjectAct(from, to),
    afterCommit: nothing,
  },
);

// --- Drag and drop ---

const drag = useOutlineDrag({
  chapters,
  outlineRows,
  rowKey,
  chapterPrimaryPath,
  collapsedChapters,
  setCollapsedChapters: (next) => void workspace.onSetCollapsedChapters(next),
  openNewChapterDialog: () => void onNewFile(),
});

const isRenaming = computed(
  () =>
    isJust(actRename.renaming.value) ||
    isJust(chapterRename.renaming.value) ||
    isJust(sceneRename.renaming.value),
);
watch(isRenaming, (renaming) => {
  if (renaming) drag.cleanup();
});

/** The chapter a new scene would go into: the one you're writing in. Without
 * one there's nothing to add a scene to, so that menu entry stays disabled. */
const activeChapterFolder = computed(() =>
  chapterFolderFromScenePath(fromMaybe(activeBasename.value) ?? ""),
);

function manuscriptHeaderNew(): Maybe<OutlineHeaderNew> {
  return just({
    label: "New file",
    run: onNewFile,
    items: just([
      { label: "New act", run: () => openCreate("act"), disabled: false },
      {
        label: "New chapter",
        run: () => openCreate("chapter"),
        disabled: false,
      },
      {
        label: "New scene",
        disabled: isNothing(activeChapterFolder.value),
        run: () => {
          if (isJust(activeChapterFolder.value)) {
            void workspace.onNewProjectScene(activeChapterFolder.value.value);
          }
        },
      },
      {
        label: "Include map",
        disabled: availableMapsForBook.value.length === 0,
        run: openIncludeMap,
      },
    ]),
  });
}

/** keep-alive reuses this pane across scene changes; register on activate so
 * returning from Notes/Storyboard restores the Writing "+" menu. */
function registerOutlineChrome(): void {
  outlineActions.headerNew =
    structureLocked.value === false ? manuscriptHeaderNew() : nothing;
  outlineActions.cancelCreating = just(() => {
    cancelCreate();
  });
}

function clearOutlineChrome(): void {
  outlineActions.headerNew = nothing;
  outlineActions.cancelCreating = nothing;
}

onMounted(registerOutlineChrome);
onActivated(registerOutlineChrome);
onDeactivated(clearOutlineChrome);

// `headerNew` is a plain object in a shared store, so the disabled state has to
// be pushed rather than pulled.
watch([activeChapterFolder, availableMapsForBook], ([folder, maps]) => {
  matchMaybe(outlineActions.headerNew, {
    nothing: () => {},
    just: (action) => {
      if (isNothing(action.items)) return;
      const scene = toMaybe(
        action.items.value.find((i) => i.label === "New scene"),
      );
      if (isJust(scene)) {
        scene.value.disabled = isNothing(folder);
      }
      const includeMapItem = toMaybe(
        action.items.value.find((i) => i.label === "Include map"),
      );
      if (isJust(includeMapItem)) {
        includeMapItem.value.disabled = maps.length === 0;
      }
    },
  });
});

watch(structureLocked, (locked) => {
  if (locked) {
    chapterRename.cancel();
    sceneRename.cancel();
    actRename.cancel();
    cancelCreate();
    outlineActions.headerNew = nothing;
    return;
  }
  outlineActions.headerNew = manuscriptHeaderNew();
});

onUnmounted(() => {
  clearOutlineChrome();
  drag.cleanup();
});
</script>

<template>
  <nav class="zw-outline-chapters" aria-label="Project files">
    <div class="zw-outline-nav zw-scroll">
      <div v-if="outlineRows.length === 0" class="zw-outline-empty">
        <p>No documents yet.</p>
        <ZwButton
          v-if="structureLocked === false"
          size="sm"
          variant="primary"
          @click="onNewFile"
        >
          New file
        </ZwButton>
      </div>
      <ul v-else class="file-list">
        <li
          v-for="row in outlineRows"
          :key="rowKey(row)"
          class="file-item"
          :class="[
            row.kind === 'act' ? 'act-item' : null,
            row.kind === 'scene' ? 'scene-item' : null,
            row.kind === 'map' ? 'map-item' : null,
            row.kind !== 'act' && row.kind !== 'map' && isJust(row.actFolder)
              ? 'in-act-item'
              : null,
            {
              // For a chapter row, row.path is its *first* scene — only treat
              // the chapter itself as active when it has just that one scene;
              // once it has 2+, only the specific scene row should highlight.
              'file-item-active':
                row.kind === 'act'
                  ? isJust(selectedActFolder) &&
                    selectedActFolder.value === row.act.folder
                  : row.kind === 'map'
                    ? isMapRowActive(row)
                    : row.kind === 'chapter'
                      ? isJust(activeFile) &&
                        row.path === activeFile.value &&
                        row.chapter.scenes.length <= 1
                      : isJust(activeFile) && row.path === activeFile.value,
              'file-item-drag':
                isJust(drag.dragFrom.value) &&
                drag.dragFrom.value.value.key === rowKey(row),
              'file-item-over':
                isJust(drag.dragOver.value) &&
                drag.dragOver.value.value === rowKey(row) &&
                (isNothing(drag.dragFrom.value) ||
                  drag.dragFrom.value.value.key !== rowKey(row)),
            },
          ]"
          :data-row-key="rowKey(row)"
          :data-row-kind="row.kind"
          :data-chapter-folder="
            row.kind === 'act'
              ? row.act.folder
              : row.kind === 'map'
                ? row.id
                : row.chapter.folder
          "
          :data-act-folder="
            toDomAttr(
              row.kind === 'act' || row.kind === 'map'
                ? nothing
                : row.actFolder,
            )
          "
          :data-scene-file="
            toDomAttr(row.kind === 'scene' ? just(row.scene.file) : nothing)
          "
          @contextmenu="
            row.kind === 'act'
              ? onActContextMenu($event, row.act)
              : row.kind === 'map'
                ? onMapContextMenu($event, row)
                : row.kind === 'chapter'
                  ? onContextMenu($event, row.chapter)
                  : onSceneContextMenu(
                      $event,
                      row.chapter.folder,
                      row.scene.file,
                      sceneRenameSeed(row.scene),
                    )
          "
          @pointerdown="
            structureLocked === false &&
            isRenaming === false &&
            drag.onRowPointerDown(dragSourceForRow(row), $event)
          "
        >
          <div class="file-row">
            <template v-if="row.kind === 'map'">
              <button
                type="button"
                class="file-link map-name"
                :title="row.title"
                @click="onSelectMap(row)"
              >
                <span class="map-badge">Map</span>
                <span class="map-title">{{ row.title }}</span>
              </button>
            </template>
            <template v-else-if="row.kind === 'act'">
              <button
                type="button"
                class="act-twist"
                :aria-label="
                  isActCollapsed(row.path) ? 'Expand act' : 'Collapse act'
                "
                @click="toggleAct(row.path)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  class="size-3"
                  :class="{ 'twist-open': isActCollapsed(row.path) === false }"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="m9 6 6 6-6 6"
                  />
                </svg>
              </button>
              <HeadingNameEdit
                v-if="
                  isJust(actRename.renaming.value) &&
                  actRename.renaming.value.value === row.act.folder
                "
                :prefix="actRenamePrefix(row.act.folder).replace(/\n$/, ': ')"
                :stacked="false"
                ><ZwInput
                  :id="nothing"
                  ref="renameActInput"
                  v-model="actRename.draft.value"
                  class="file-rename"
                  aria-label="Rename"
                  :label="nothing"
                  @keydown.enter.prevent="actRename.commit"
                  @keydown.escape.prevent="actRename.cancel"
                  @blur="actRename.commit"
              /></HeadingNameEdit>
              <button
                v-else
                type="button"
                class="act-link"
                :title="actDisplayName(row.act)"
                @click="onSelectAct(row)"
                @dblclick.stop.prevent="
                  structureLocked === false &&
                  canRenameAct(row.act.folder) &&
                  actRename.start(row.act.folder, storedChapterName(row.act))
                "
              >
                {{ actDisplayName(row.act) }}
              </button>
              <span
                v-if="
                  isNothing(actRename.renaming.value) ||
                  actRename.renaming.value.value !== row.act.folder
                "
                class="act-rule"
                aria-hidden="true"
              />
              <button
                v-if="structureLocked === false && isRenaming === false"
                type="button"
                class="act-add-chapter"
                title="Add chapter"
                aria-label="Add chapter"
                @pointerdown.stop
                @click.stop="onAddChapter(row)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  class="size-3"
                  aria-hidden="true"
                >
                  <path stroke-linecap="round" d="M12 5v14M5 12h14" />
                </svg>
              </button>
            </template>
            <template v-else-if="row.kind === 'chapter'">
              <button
                v-if="row.chapter.scenes.length > 1"
                type="button"
                class="file-twist"
                :aria-label="
                  isFileCollapsed(row.path)
                    ? 'Expand scenes'
                    : 'Collapse scenes'
                "
                @click="toggleFile(row.path)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  class="size-3"
                  :class="{ 'twist-open': isFileCollapsed(row.path) === false }"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="m9 6 6 6-6 6"
                  />
                </svg>
              </button>
              <span v-else class="file-twist-spacer" aria-hidden="true" />
              <HeadingNameEdit
                v-if="
                  isJust(chapterRename.renaming.value) &&
                  chapterRename.renaming.value.value === row.path
                "
                :prefix="
                  chapterRenamePrefix(row.chapter.folder).replace(/\n$/, ': ')
                "
                :stacked="false"
                ><ZwInput
                  :id="nothing"
                  ref="renameInput"
                  v-model="chapterRename.draft.value"
                  class="file-rename"
                  aria-label="Rename file"
                  :label="nothing"
                  @keydown.enter.prevent="chapterRename.commit"
                  @keydown.escape.prevent="chapterRename.cancel"
                  @blur="chapterRename.commit"
              /></HeadingNameEdit>
              <button
                v-else
                type="button"
                class="file-link"
                :title="chapterDisplayName(row.path)"
                @click="onSelectChapter(row)"
                @dblclick.stop.prevent="
                  structureLocked === false &&
                  canRenameChapter(row.chapter.folder) &&
                  chapterRename.start(row.path, storedChapterName(row.chapter))
                "
              >
                {{ chapterDisplayName(row.path) }}
              </button>
              <button
                v-if="structureLocked === false"
                type="button"
                class="file-add-scene"
                title="Add scene"
                aria-label="Add scene"
                @click.stop="onAddScene(row)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  class="size-3"
                  aria-hidden="true"
                >
                  <path stroke-linecap="round" d="M12 5v14M5 12h14" />
                </svg>
              </button>
            </template>
            <template v-else>
              <ZwInput
                v-if="
                  isJust(sceneRename.renaming.value) &&
                  sceneRename.renaming.value.value === row.path
                "
                :id="nothing"
                ref="renameSceneInput"
                v-model="sceneRename.draft.value"
                class="file-rename"
                aria-label="Rename scene"
                placeholder="Name this scene…"
                :label="nothing"
                @keydown.enter.prevent="sceneRename.commit"
                @keydown.escape.prevent="sceneRename.cancel"
                @blur="sceneRename.commit"
              />
              <button
                v-else
                type="button"
                class="file-link file-link-placeholder"
                :title="sceneRowLabel(row)"
                @click="onSelectFile(row.path)"
                @dblclick.stop.prevent="
                  structureLocked === false &&
                  sceneRename.start(row.path, sceneRenameSeed(row.scene))
                "
              >
                <span class="file-link-snippet">{{ sceneRowLabel(row) }}</span>
              </button>
            </template>
          </div>
        </li>
        <li
          v-if="
            isJust(drag.dragKind.value) && drag.dragKind.value.value === 'scene'
          "
          class="file-item new-chapter-drop"
          :data-row-key="'__new_chapter__'"
          :data-row-kind="'chapter'"
          :data-chapter-folder="'__new_chapter__'"
          :class="{
            'file-item-over':
              isJust(drag.dragOver.value) &&
              drag.dragOver.value.value === '__new_chapter__',
          }"
        >
          <div class="file-row">
            <span class="file-twist-spacer" aria-hidden="true" />
            <span class="new-chapter-drop-label">Move to new chapter</span>
          </div>
        </li>
        <!-- An act at the end of the manuscript owns every row below it, so
             without this there'd be no way to drop something after it. -->
        <li
          v-if="
            isJust(drag.dragKind.value) &&
            (drag.dragKind.value.value === 'chapter' ||
              drag.dragKind.value.value === 'act')
          "
          class="file-item new-chapter-drop"
          :data-row-key="'__end_of_manuscript__'"
          :data-row-kind="'chapter'"
          :data-chapter-folder="'__end_of_manuscript__'"
          :class="{
            'file-item-over':
              isJust(drag.dragOver.value) &&
              drag.dragOver.value.value === '__end_of_manuscript__',
          }"
        >
          <div class="file-row">
            <span class="file-twist-spacer" aria-hidden="true" />
            <span class="new-chapter-drop-label">
              Move to the end, outside any act
            </span>
          </div>
        </li>
      </ul>
    </div>
  </nav>

  <ZwDialog
    :open="includeMapOpen"
    title="Include map"
    :description="
      just(
        'Add a worldbuilding map to the book outline. It stays editable in Maps.',
      )
    "
    @close="closeIncludeMap"
  >
    <ul
      v-if="availableMapsForBook.length > 0"
      class="include-map-list"
      role="listbox"
      aria-label="Maps to include"
    >
      <li v-for="name in availableMapsForBook" :key="name">
        <button
          type="button"
          class="include-map-item"
          role="option"
          @click="includeMap(name)"
        >
          {{ displayFileName(name) }}
        </button>
      </li>
    </ul>
    <p v-else class="include-map-empty">
      No maps left to include. Create one under Worldbuilding, or remove a map
      from the outline first.
    </p>
    <template #footer>
      <ZwButton @click="closeIncludeMap">Cancel</ZwButton>
    </template>
  </ZwDialog>
</template>

<style scoped>
.zw-outline-nav {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 var(--space-sm) var(--space-md);
}

.zw-outline-chapters {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.zw-outline-chapters > .zw-outline-nav {
  flex: 1;
  min-height: 0;
  padding-bottom: var(--space-sm);
}

.zw-outline-empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-sm);
  margin: var(--space-sm) var(--space-sm) 0;
  font-size: 0.9rem;
  line-height: 1.4;
  color: color-mix(in oklch, var(--text-muted) 85%, transparent);
}

.zw-outline-empty p {
  margin: 0;
}

.file-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.file-item {
  margin: 0.15rem 0;
  border-radius: 0.3rem;
}

.file-item-active {
  background: color-mix(in oklch, var(--color-zw-amber) 26%, transparent);
}

.file-item-over {
  outline: 1px dashed
    color-mix(in oklch, var(--color-zw-amber) 55%, transparent);
}

.file-item-drag {
  opacity: 0.55;
}

.file-row {
  display: flex;
  align-items: center;
  gap: 0.1rem;
  min-width: 0;
  /* The whole row is a drag source now, not just the grip icon. */
  cursor: grab;
}

.file-item-drag .file-row,
.file-row:active {
  cursor: grabbing;
}

.file-twist {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--text-muted);
  width: 1.25rem;
  height: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 0.2rem;
  flex-shrink: 0;
}

.file-twist:hover {
  color: var(--text-strong);
}

.file-twist-spacer {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
}

.twist-open {
  transform: rotate(90deg);
}

.file-add-scene,
.act-add-chapter {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--text-muted);
  width: 1.25rem;
  height: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 0.2rem;
  flex-shrink: 0;
}

/* Avoid opacity compositing for hover-only icons while keeping the button
 * in the keyboard tab order. */
.file-add-scene > svg,
.act-add-chapter > svg {
  visibility: hidden;
}

.file-item:hover .file-add-scene > svg,
.file-add-scene:focus-visible > svg,
.file-item:hover .act-add-chapter > svg,
.act-add-chapter:focus-visible > svg {
  visibility: visible;
}

.file-add-scene:hover,
.act-add-chapter:hover {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.file-link {
  appearance: none;
  border: 0;
  background: transparent;
  min-width: 0;
  flex: 1;
  padding: 0.28rem 0.35rem;
  text-align: left;
  font: inherit;
  font-size: 0.92rem;
  color: var(--text);
  cursor: pointer;
  border-radius: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-link:hover {
  color: var(--text-strong);
}

/* Nameless scene: its opening words (or "Empty scene") stand in for a name,
 * muted italic, wrapping to two lines so there's enough prose to recognise. */
.file-link.file-link-placeholder {
  white-space: normal;
  font-size: 0.86rem;
  line-height: 1.3;
  font-style: italic;
  color: var(--text-muted);
}

.file-item-active .file-link.file-link-placeholder {
  color: var(--text-muted-on-accent);
}

.file-link-snippet {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.file-link.file-link-placeholder:hover {
  color: var(--text);
}

.file-row :deep(.file-rename.zw-input-shell) {
  flex: 1;
  min-width: 0;
  width: auto;
  --rename-height: 1.75rem;
  height: var(--rename-height);
  min-height: 0;
  margin: 0 0.2rem;
  font-size: 0.92rem;
}
.act-item .file-row {
  min-height: 1.75rem;
}
.act-item :deep(.file-rename.zw-input-shell) {
  --rename-height: 1.25rem;
  font-size: 0.78rem;
}
.file-row :deep(.file-rename .zw-input),
.file-row :deep(.file-rename .zw-input-face),
.file-row :deep(.file-rename .zw-input-face-text) {
  font-size: inherit;
  line-height: calc(var(--rename-height) - 2px);
}
.file-row :deep(.file-rename .zw-input),
.file-row :deep(.file-rename .zw-input-face) {
  padding: 0 0.35rem;
}

/* Scenes are flat siblings of chapters in the DOM (not nested inside a
   chapter's own <li> — that nesting made drag hit-testing unreliable), so
   the "nested under its chapter" look is just an indent on the row itself. */
.scene-item {
  /* Chapter text starts at twist(1.25) + gap(0.1) + link padding(0.35) =
     1.7rem from the row edge; indent scenes clearly past that so they read
     as nested under their chapter, not just slightly offset from it. */
  margin-left: 2.6rem;
}

/* An act reads as a labeled divider rather than another clickable file: it
   holds no prose, so it should look like a section break in the list. */
.act-item {
  margin-top: var(--space-sm);
}

.act-item:first-child {
  margin-top: 0.15rem;
}

.act-twist {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--text-muted);
  width: 1.25rem;
  height: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 0.2rem;
  flex-shrink: 0;
}

.act-twist:hover {
  color: var(--text-strong);
}

.act-link {
  appearance: none;
  border: 0;
  background: transparent;
  min-width: 0;
  padding: 0.28rem 0.35rem;
  text-align: left;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.act-link:hover {
  color: var(--text-strong);
}

/* The rule runs from the title to the edge of the pane, which is what makes
   the row read as a divider instead of a heading floating in the list. */
.act-rule {
  flex: 1;
  min-width: 0.75rem;
  height: 1px;
  margin-right: 0.35rem;
  background: var(--border);
}

.act-item.file-item-active .act-link {
  color: var(--text-strong);
}

.map-badge {
  display: inline-block;
  margin-right: 0.35rem;
  font-size: 0.65rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
  border: 1px solid var(--border);
  border-radius: 0.2rem;
  padding: 0.05rem 0.3rem;
  vertical-align: middle;
}

.map-name {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.map-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Chapters (and their scenes) sitting inside an act step in, so the act's
   span is readable at a glance. */
.in-act-item {
  margin-left: 0.85rem;
}

.scene-item.in-act-item {
  margin-left: 3.45rem;
}

.new-chapter-drop {
  margin: 0.15rem 0;
  border: 1px dashed color-mix(in oklch, var(--color-zw-amber) 45%, transparent);
  border-radius: 0.3rem;
}

.new-chapter-drop-label {
  flex: 1;
  min-width: 0;
  padding: 0.28rem 0.35rem;
  font-size: 0.88rem;
  color: var(--text-muted);
}

.include-map-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  max-height: min(16rem, 50svh);
  overflow: auto;
}

.include-map-item {
  display: block;
  width: 100%;
  margin: 0;
  padding: 0.45rem 0.55rem;
  border: 1px solid transparent;
  border-radius: 0.3rem;
  background: transparent;
  color: var(--text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.include-map-item:hover {
  background: var(--hover-fill);
}

.include-map-item:focus-visible {
  outline: 2px solid color-mix(in oklch, var(--color-zw-amber) 55%, transparent);
  outline-offset: 1px;
}

.include-map-empty {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
  color: var(--text-muted);
}
</style>
